import React, { useRef } from 'react';
import { ArrowLeftIcon, ArrowRightIcon, ArrowUpRightIcon, StarIcon } from 'lucide-react';
import { reviews } from '../data/content';
import { googleReviews } from '../data/site';
import { SectionLabel } from './ui/SectionLabel';
import { Reveal } from './ui/Reveal';
export function Reviews() {
  const trackRef = useRef<HTMLDivElement>(null);
  function scrollBy(direction: 1 | -1) {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({
      left: direction * Math.round(el.clientWidth * 0.8),
      behavior: 'smooth'
    });
  }
  return <section id="reviews" className="scroll-mt-24 bg-white py-24 lg:py-40" data-id="element-393" data-mp="309dkb8bcslsy">
      <div className="mx-auto max-w-shell px-5 sm:px-8 lg:px-12" data-id="element-394" data-mp="352q85g87kokn">
        <Reveal className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between" data-id="element-395" data-mp="3a1t4mca4hmh0">
          <div className="max-w-[720px]" data-id="element-396" data-mp="3f0w6xoypxela">
            <SectionLabel data-id="element-397" data-mp="3f1xh7r7wtx1y">What our patients say</SectionLabel>
            <h2 className="font-display mt-6 text-[clamp(2rem,4.2vw,3.3rem)] leading-[1.06] text-ink" data-id="element-398" data-mp="3jynzml76t4qj">
              Trust is earned.
              <br data-id="element-399" data-mp="2re8kl6ca49lq" />
              For 35 years, that is how we have built it.
            </h2>
            <p className="mt-5 max-w-[46ch] text-[16.5px] leading-[1.8] text-ink/60" data-id="element-400" data-mp="2mbzq25jrczhi">
              Patients who come back. Families who recommend us.
            </p>
          </div>

          <div className="flex items-center gap-3 lg:hidden" data-id="element-401" data-mp="2ddxsy135aqzi">
            <RoundButton label="Previous review" onClick={() => scrollBy(-1)} icon={ArrowLeftIcon} data-id="element-402" data-mp="3a2vp9nqjmwys" />
            <RoundButton label="Next review" onClick={() => scrollBy(1)} icon={ArrowRightIcon} data-id="element-403" data-mp="354c2jl44lh5d" />
          </div>
        </Reveal>

        <div ref={trackRef} className="no-scrollbar mt-14 flex snap-x snap-mandatory gap-5 overflow-x-auto pb-2 lg:mt-20 lg:grid lg:grid-cols-3 lg:gap-8 lg:overflow-visible" data-id="element-404" data-mp="30cai3bwt0k49">
          {reviews.map(review => <figure key={review.name} className="glass-subtle flex w-[85vw] shrink-0 snap-start flex-col justify-between rounded-[14px] p-8 sm:w-[380px] lg:w-auto" data-id="element-405" data-mp="3svr8roz0wilw">
              <div data-id="element-406" data-mp="1m7ql14studoc">
                <div className="flex gap-1" aria-label="Rated 5 out of 5 stars" data-id="element-407" data-mp="1r47jartqq8f5">
                  {Array.from({
                length: 5
              }).map((_, i) => <StarIcon key={i} aria-hidden="true" className="h-3.5 w-3.5 fill-accent text-accent" data-id="element-408" data-mp="1cf1mfaidkuaf" data-mpi={i} />)}
                </div>
                <blockquote className="mt-6 text-[16px] leading-[1.8] text-ink/80" data-id="element-409" data-mp="13h937v9j92hr">
                  “{review.quote}”
                </blockquote>
              </div>
              <figcaption className="mt-8 flex items-center gap-3.5 border-t border-ink/[0.09] pt-6" data-id="element-410" data-mp="ti9vqg0i7cwc">
                <span aria-hidden="true" className="flex h-11 w-11 items-center justify-center rounded-full border border-ink/15 text-[15px] font-semibold text-ink" data-id="element-411" data-mp="yg1y63yryhes">
                  {review.name.charAt(0)}
                </span>
                <span data-id="element-412" data-mp="h796p7adqsvx">
                  <span className="block text-[15px] font-medium text-ink" data-id="element-413" data-mp="h4ekba7t2xzv">{review.name}</span>
                  <span className="block text-[13px] text-ink/50" data-id="element-414" data-mp="c97logte8jht">{review.meta}</span>
                </span>
              </figcaption>
            </figure>)}
        </div>

        <Reveal className="mt-10 flex flex-col gap-3 border-t border-ink/10 pt-8 sm:flex-row sm:items-center sm:justify-between" data-id="element-415" data-mp="m4sjs3vcczma">
          <p className="text-[15px] text-ink/70" data-id="element-416" data-mp="zz1umnaldnws">
            <strong className="font-semibold text-ink" data-id="element-417" data-mp="vuc1v7b8cpg3">{googleReviews.rating}</strong> out of 5 on
            Google, from {googleReviews.count} reviews by Miami Lakes patients.
          </p>
          <a href={googleReviews.url} target="_blank" rel="noreferrer" className="group inline-flex min-h-[44px] items-center gap-2 text-[14.5px] font-medium text-ink" data-id="element-418" data-mp="14vsbvdy4upjs">
            <span className="nav-link" data-id="element-419" data-mp="36ozq0k20f143">See more reviews on Google</span>
            <ArrowUpRightIcon aria-hidden="true" className="h-4 w-4 text-accent transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={1.75} data-id="element-420" data-mp="385diteu24c95" />
          </a>
        </Reveal>
      </div>
    </section>;
}
function RoundButton({
  label,
  onClick,
  icon: Icon
}: {
  label: string;
  onClick: () => void;
  icon: React.ElementType;
}) {
  return <button type="button" onClick={onClick} aria-label={label} className="flex h-12 w-12 items-center justify-center rounded-full border border-ink/15 text-ink transition-colors hover:bg-ink hover:text-white" data-id="element-421" data-mp="2pbzxm1g5bk6q">
      <Icon aria-hidden="true" className="h-4 w-4" strokeWidth={1.75} data-id="element-422" data-mp="343ilsmj5sl16" />
    </button>;
}